import React, { useCallback, useEffect, useMemo, useState } from 'react'

import clsx from 'clsx'
import range from 'lodash/range'

import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import ProtipBanner from '$extensionSrc/components/Sidebar/RecommendationCards/RecommendationCardContainer/RecommendationCard/ProtipBanner'
import { getPlatformName } from '$extensionSrc/data/platform/name'
import {
  BlueskySettingKey,
  FacebookSettingKey,
  LinkedinSettingKey,
  XSettingKey,
} from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { RecommendationKeyEnum } from '$extensionSrc/recommendations/recommendationTypes'
import { updateRecommendation } from '$extensionSrc/state'
import {
  getElapsedMonthsByYear,
  getOlderThanMonthYear,
  getRemainingMonthsInYear,
  isMoreThanNumMonthsAgo,
  MONTHS,
  OLDER_THAN_OPTION,
} from '$extensionSrc/utils/dateUtils'
import { PLATFORM } from '$extensionSrc/utils/enums'

import styles from './SelectDeleteTimeframe.module.scss'

function getStatusMessage(
  selectedDateOption: OLDER_THAN_OPTION,
  year: number,
  month: number,
  numPosts: any,
  mostRecentPostDate: any,
  platform: PLATFORM,
  count: any,
) {
  const platformName = getPlatformName(platform)

  if (numPosts) {
    if (parseInt(numPosts, 10) > 1000) {
      if (selectedDateOption === OLDER_THAN_OPTION.NOW) {
        return `${platformName} may limit our ability to reach posts that far back`
      } else if (mostRecentPostDate) {
        const isMoreThanAYearAgo = isMoreThanNumMonthsAgo(
          new Date(year, month).getTime(),
          12,
          Date.parse(mostRecentPostDate),
        )
        if (isMoreThanAYearAgo) {
          return `${platformName} may limit our ability to reach posts that far back`
        }
      } else {
        return `${platformName} may limit our ability to reach older posts`
      }
    } else if (mostRecentPostDate) {
      // Not more than 1000 posts, use more than 5 years ago rather than 1
      const isMoreThanFiveYearsAgo = isMoreThanNumMonthsAgo(
        new Date(year, month).getTime(),
        60,
        Date.parse(mostRecentPostDate),
      )

      if (isMoreThanFiveYearsAgo) {
        return `${platformName} may limit our ability to reach posts that far back`
      }
      return `Deleted ${count || 0} total posts.`
    } else {
      return `Deleted ${count || 0} total posts.`
    }
  }
  return null
}

export default function SelectDeleteTimeframe({
  locked,
  platform,
  platformUserId,
  recommendationKey,
  updateTaskButtonText,
  updateFooterText,
  initialNumPosts,
  initialMostRecentPostDate,
  initialCountDeleted,
  settingsSelected,
  selectedDateOption = OLDER_THAN_OPTION.NOW,
  settingKey,
  selectedMonth = new Date().getMonth().toString(),
  selectedYear = new Date().getFullYear().toString(),
  minYear,
  minMonth,
  title = 'How old are the posts you want to delete?',
  subtitle = null,
}: {
  locked: boolean
  platform: PLATFORM
  platformUserId: string
  recommendationKey: RecommendationKeyEnum
  updateTaskButtonText: (text: string) => void
  updateFooterText: (text: string) => void
  initialNumPosts: string
  initialMostRecentPostDate: string
  initialCountDeleted: string
  settingsSelected: any
  settingKey?:
    | BlueskySettingKey
    | XSettingKey
    | FacebookSettingKey
    | LinkedinSettingKey
  selectedDateOption: OLDER_THAN_OPTION
  selectedMonth: string
  selectedYear: string
  minYear: number
  minMonth: number // 0-indexed month
  title?: string
  subtitle?: string
}) {
  const [showDryTestBanner, setShowDryTestBanner] = useState(false)
  const resetBanner = () => setShowDryTestBanner(false)

  const [hasVotedForDryTest, setHasVotedForDryTest] = useState(false)

  const voteForDryTest = useCallback(() => {
    setShowDryTestBanner(true)
    setHasVotedForDryTest(true)
  }, [])

  const [month, year] = useMemo(
    () =>
      getOlderThanMonthYear(selectedDateOption, selectedMonth, selectedYear),
    [selectedDateOption, selectedMonth, selectedYear],
  )

  useEffect(() => {
    if (selectedDateOption !== OLDER_THAN_OPTION.NOW) {
      updateTaskButtonText(`before ${MONTHS[month]} 1, ${year}`)
    } else {
      updateTaskButtonText('')
    }
  }, [updateTaskButtonText, selectedDateOption, month, year])

  const saveSelectedDates = useCallback(
    (d, m, y) => {
      if (settingKey) {
        updateRecommendation(
          platform,
          platformUserId,
          recommendationKey,
          {
            settingsSelected: {
              ...settingsSelected,
              [settingKey]: {
                ...settingsSelected[settingKey],
                dateOption: d,
                sinceDateMonth: m,
                sinceDateYear: y,
              },
            },
          },
          { doNotUpdateLastUpdatedTimestamp: true },
        )
      }
    },
    [platform, platformUserId, recommendationKey, settingKey, settingsSelected],
  )

  useEffect(() => {
    if (initialNumPosts) {
      const statusMessage = getStatusMessage(
        selectedDateOption,
        year,
        month,
        initialNumPosts,
        initialMostRecentPostDate,
        platform,
        initialCountDeleted,
      )
      updateFooterText(statusMessage)
    }
  }, [
    selectedDateOption,
    year,
    month,
    platform,
    initialNumPosts,
    initialMostRecentPostDate,
    initialCountDeleted,
    updateFooterText,
  ])

  const months = useMemo(() => {
    if (selectedYear === minYear.toString()) {
      return getRemainingMonthsInYear(minMonth)
    }
    return getElapsedMonthsByYear(selectedYear)
  }, [selectedYear, minYear, minMonth])

  const handleSelectOption = useCallback(
    (event) => {
      resetBanner()
      const val = event.target.value
      saveSelectedDates(val, selectedMonth, selectedYear)
    },
    [saveSelectedDates, selectedMonth, selectedYear],
  )

  const handleSetMonth = useCallback(
    (event) => {
      const targetMonth = event.target.value
      saveSelectedDates(selectedDateOption, targetMonth, selectedYear)
    },
    [selectedDateOption, selectedYear, saveSelectedDates],
  )

  const handleSetYear = useCallback(
    (event) => {
      const targetYear = event.target.value
      saveSelectedDates(selectedDateOption, selectedMonth, targetYear)
    },
    [selectedDateOption, selectedMonth, saveSelectedDates],
  )

  return (
    <div className={clsx(styles.selectDeleteTimeframe)}>
      <div className="body1 black bold">
        {title}
        {subtitle && <div className="body2 black">{subtitle}</div>}
      </div>
      <div className={clsx(styles.cardBody)}>
        <div className={clsx(styles.selectDate, 'body2')}>
          <select
            className={clsx(styles.dateDropdown)}
            disabled={locked}
            id="dateSelector"
            name="date"
            onChange={handleSelectOption}
            value={selectedDateOption}
            aria-label="Select a date option"
          >
            <option value={OLDER_THAN_OPTION.NOW}>All</option>
            <option value={OLDER_THAN_OPTION.ONE_MONTH}>
              Older than 1 month
            </option>
            <option value={OLDER_THAN_OPTION.TWO_MONTHS}>
              Older than 2 months
            </option>
            <option value={OLDER_THAN_OPTION.THREE_MONTHS}>
              Older than 3 months
            </option>
            <option value={OLDER_THAN_OPTION.CHOOSE_MONTH_YEAR}>
              Choose a month and year...
            </option>
          </select>
        </div>
        {selectedDateOption === OLDER_THAN_OPTION.CHOOSE_MONTH_YEAR &&
          !locked && (
            <div className={clsx(styles.monthYearSelector, 'body2')}>
              <div>Before</div>
              <select
                id="monthSelector"
                name="month"
                onChange={handleSetMonth}
                value={selectedMonth}
              >
                {months.map((m) => (
                  <option value={m} key={m}>
                    {MONTHS[m]}
                  </option>
                ))}
              </select>
              <select
                id="yearSelector"
                name="year"
                onChange={handleSetYear}
                value={selectedYear}
              >
                {range(new Date().getFullYear(), minYear - 1).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}
        {selectedDateOption !== OLDER_THAN_OPTION.NOW &&
          selectedDateOption !== OLDER_THAN_OPTION.CHOOSE_MONTH_YEAR &&
          !locked && (
            <>
              <div className="body2">
                <div className={clsx(styles.rollingDeleteOption)}>
                  <input type="radio" checked readOnly /> Just this once
                </div>
                <div className={clsx(styles.rollingDeleteOption, 'dark-gray')}>
                  <input type="radio" disabled />
                  <div>
                    Automatically on a rolling basis{' '}
                    {!hasVotedForDryTest && (
                      <InteractiveElement
                        aria-label={`I want ${selectedDateOption} month rolling deletes`}
                        onClick={voteForDryTest}
                      >
                        — <span className="link2 purple">I want this</span>
                      </InteractiveElement>
                    )}
                  </div>
                </div>
              </div>
              {showDryTestBanner && (
                <ProtipBanner hideIcon variant="blue">
                  Thanks for letting us know! We'll alert you when this feature
                  is available. In the meantime, you can come back and delete
                  your old posts as regularly as you need to
                </ProtipBanner>
              )}
            </>
          )}
      </div>
    </div>
  ) // past year selection cuts at twitter being founded in 2006
}
